import {
	isActiveSketchEntityReferenceRecord,
	isRetainedSketchEntityReferenceRecord,
	type SketchEntityReferenceRecord,
	type SketchReferenceStatus,
} from '../model/SketchEntityReferenceRecord';
import type {
	SketchEntityReferenceRepositoryWriteScope,
	SketchEntityReferenceTopologyOwnerQueryInput,
} from './SketchEntityReferenceRepository';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import {
	assertSketchEntityPersistentId,
	assertSketchPersistentId,
} from '../../../sketch/identity/SketchPersistentIdFactory';

/**
 * 内存草图实体引用存储。
 *
 * 说明：
 * 1. 每个存储实例独立持有一份引用记录 Map；
 * 2. 正式仓库和事务工作区复用同一套基础增删改查语义；
 * 3. 该 class 不负责事务调度或历史同步入口，所有事务内查询均直接读取自身快照。
 */
export class InMemorySketchEntityReferenceStore implements SketchEntityReferenceRepositoryWriteScope {
	/** 当前作用域独立持有的引用记录。 */
	private readonly references = new Map<string, SketchEntityReferenceRecord>();

	/**
	 * 深拷贝引用记录，阻止调用者和作用域内部共享嵌套对象。
	 *
	 * @param reference 引用记录。
	 * @returns plain data 深拷贝。
	 */
	private cloneReference(reference: SketchEntityReferenceRecord): SketchEntityReferenceRecord {
		return JSON.parse(JSON.stringify(reference)) as SketchEntityReferenceRecord;
	}

	/**
	 * 基于目标草图和目标实体构建唯一性 key。
	 *
	 * @param reference 引用目标信息。
	 * @returns 目标唯一性 key。
	 */
	private buildTargetKey(
		reference: Pick<SketchEntityReferenceRecord, 'targetSketchId' | 'targetEntityId'>,
	): string {
		return `${reference.targetSketchId}::${reference.targetEntityId}`;
	}

	/**
	 * 校验引用记录中所有草图身份均已进入持久 ID 空间。
	 *
	 * topology 来源使用自身的 owner/locator 身份规则，不在这里按草图 ID 校验。
	 * 仓储是引用记录进入运行时状态的统一入口，因此异步保存、事务保存与历史恢复
	 * 都会经过同一条校验链路，不保留运行时 UUID 兼容分支。
	 *
	 * @param reference 待写入的引用记录。
	 */
	private assertPersistentSketchIdentity(reference: SketchEntityReferenceRecord): void {
		assertSketchPersistentId(reference.targetSketchId, 'targetSketchId');
		assertSketchEntityPersistentId(reference.targetEntityId, 'targetEntityId');

		if (reference.sourceDescriptor.kind !== 'sketch-entity') {
			return;
		}

		assertSketchPersistentId(
			reference.sourceDescriptor.sourceSketchId,
			'sourceDescriptor.sourceSketchId',
		);
		assertSketchEntityPersistentId(
			reference.sourceDescriptor.entityId,
			'sourceDescriptor.entityId',
		);
	}

	/**
	 * 将来源存储的完整状态深拷贝到目标存储。
	 *
	 * 说明：先完成全部深拷贝再清空目标，避免复制失败时破坏目标原状态。
	 *
	 * @param source 来源存储。
	 * @param target 目标存储。
	 */
	protected copyReferencesState(
		source: InMemorySketchEntityReferenceStore,
		target: InMemorySketchEntityReferenceStore,
	): void {
		const nextEntries = Array.from(source.references.entries()).map(([referenceId, reference]) => (
			[referenceId, this.cloneReference(reference)] as const
		));

		target.references.clear();
		nextEntries.forEach(([referenceId, reference]) => {
			target.references.set(referenceId, reference);
		});
	}

	/**
	 * 筛选当前作用域记录并返回深拷贝。
	 *
	 * @param predicate 记录筛选条件。
	 * @returns 满足条件的引用记录深拷贝。
	 */
	protected filterReferences(
		predicate: (reference: SketchEntityReferenceRecord) => boolean,
	): SketchEntityReferenceRecord[] {
		return Array.from(this.references.values())
			.filter(predicate)
			.map((reference) => this.cloneReference(reference));
	}

	/**
	 * 同步读取单条引用记录副本。
	 *
	 * 该入口供正式仓库向同步历史与移动解除引用端口暴露读取能力，事务工作区仍只使用异步接口。
	 *
	 * @param referenceId 引用记录 ID。
	 * @returns 命中时返回深拷贝记录，否则返回 null。
	 */
	protected loadReferenceImmediately(referenceId: string): SketchEntityReferenceRecord | null {
		const reference = this.references.get(referenceId);
		return reference ? this.cloneReference(reference) : null;
	}

	/**
	 * 同步保存引用记录并执行现有目标唯一性校验。
	 *
	 * @param references 引用记录集合。
	 */
	protected saveReferencesImmediately(references: SketchEntityReferenceRecord[]): void {
		references.forEach((reference) => {
			this.assertPersistentSketchIdentity(reference);
		});
		const nextEntries = references.map((reference) => this.cloneReference(reference));
		const nextRetainedEntries = nextEntries.filter(isRetainedSketchEntityReferenceRecord);
		const nextTargetKeys = new Set<string>();

		nextRetainedEntries.forEach((reference) => {
			const targetKey = this.buildTargetKey(reference);
			if (nextTargetKeys.has(targetKey)) {
				throw new Error(`Duplicate target reference in save batch: ${targetKey}`);
			}
			nextTargetKeys.add(targetKey);
		});

		nextRetainedEntries.forEach((reference) => {
			const targetKey = this.buildTargetKey(reference);
			const conflict = Array.from(this.references.values()).find((candidate) => (
				isRetainedSketchEntityReferenceRecord(candidate)
					&& candidate.referenceId !== reference.referenceId
					&& this.buildTargetKey(candidate) === targetKey
			));
			if (conflict) {
				throw new Error(`Target already has a living reference: ${targetKey}`);
			}
		});

		nextEntries.forEach((reference) => {
			this.references.set(reference.referenceId, reference);
		});
	}

	/**
	 * 同步删除引用记录。
	 *
	 * @param referenceIds 引用记录 ID 集合。
	 */
	protected removeReferencesImmediately(referenceIds: string[]): void {
		referenceIds.forEach((referenceId) => {
			this.references.delete(referenceId);
		});
	}

	/**
	 * 保存引用记录。
	 *
	 * @param references 引用记录集合。
	 */
	public async saveReferences(references: SketchEntityReferenceRecord[]): Promise<void> {
		this.saveReferencesImmediately(references);
	}

	/**
	 * 按 ID 读取引用记录。
	 *
	 * @param referenceIds 引用记录 ID 集合。
	 * @returns 按输入顺序返回的命中记录深拷贝。
	 */
	public async loadReferences(referenceIds: string[]): Promise<SketchEntityReferenceRecord[]> {
		return referenceIds
			.map((referenceId) => this.references.get(referenceId))
			.filter((reference): reference is SketchEntityReferenceRecord => reference !== undefined)
			.map((reference) => this.cloneReference(reference));
	}

	/**
	 * 按来源草图读取仍存储的草图来源引用记录。
	 *
	 * @param sourceSketchId 来源草图 ID。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesBySourceSketch(
		sourceSketchId: string,
	): Promise<SketchEntityReferenceRecord[]> {
		return this.filterReferences((reference) => (
			reference.sourceDescriptor.kind === 'sketch-entity'
				&& reference.sourceDescriptor.sourceSketchId === sourceSketchId
		));
	}

	/**
	 * 按来源草图实体读取仍存储的草图来源引用记录。
	 *
	 * @param sourceSketchId 来源草图 ID。
	 * @param sourceEntityId 来源实体 ID。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesBySourceEntity(
		sourceSketchId: string,
		sourceEntityId: string,
	): Promise<SketchEntityReferenceRecord[]> {
		return this.filterReferences((reference) => (
			reference.sourceDescriptor.kind === 'sketch-entity'
				&& reference.sourceDescriptor.sourceSketchId === sourceSketchId
				&& reference.sourceDescriptor.entityId === sourceEntityId
		));
	}

	/**
	 * 按 topology owner 读取当前工作副本中的 active 引用记录。
	 *
	 * @param input topology owner 匹配条件。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesByTopologyOwner(
		input: SketchEntityReferenceTopologyOwnerQueryInput,
	): Promise<SketchEntityReferenceRecord[]> {
		return this.filterReferences((reference) => (
			(input.statusScope === 'retained'
				? isRetainedSketchEntityReferenceRecord(reference)
				: isActiveSketchEntityReferenceRecord(reference))
			&& reference.sourceDescriptor.kind === 'topology-edge'
			&& reference.sourceDescriptor.owner.ownerKind === input.ownerKind
			&& reference.sourceDescriptor.owner.ownerId === input.ownerId
			&& (
				input.matchAnyBodyBinding === true
				|| (reference.sourceDescriptor.owner.bodyBindingId ?? null)
					=== (input.bodyBindingId ?? null)
			)
		));
	}

	/**
	 * 按同一草图内的一批来源或目标实体读取全部引用记录。
	 *
	 * 设计说明：一次扫描同时覆盖来源和目标，避免删除复合实体时按每个实体 ID 重复遍历仓库。
	 *
	 * @param input 草图与实体 ID 集合。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesBySketchEntities(input: {
		/** 草图 ID。 */
		sketchId: string;
		/** 同一删除批次中的实体 ID。 */
		entityIds: readonly string[];
	}): Promise<SketchEntityReferenceRecord[]> {
		const entityIdSet = new Set(input.entityIds);
		if (entityIdSet.size === 0) {
			return [];
		}

		return this.filterReferences((reference) => (
			(
				(
					reference.targetSketchId === input.sketchId
					&& entityIdSet.has(reference.targetEntityId)
				)
				|| (
					reference.sourceDescriptor.kind === 'sketch-entity'
					&& reference.sourceDescriptor.sourceSketchId === input.sketchId
					&& entityIdSet.has(reference.sourceDescriptor.entityId)
				)
			)
		));
	}

	/**
	 * 按目标草图读取 active 引用记录。
	 *
	 * @param targetSketchId 目标草图 ID。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesByTargetSketch(
		targetSketchId: string,
		statusScope: 'active' | 'retained' = 'active',
	): Promise<SketchEntityReferenceRecord[]> {
		return this.filterReferences((reference) => (
			(statusScope === 'retained'
				? isRetainedSketchEntityReferenceRecord(reference)
				: isActiveSketchEntityReferenceRecord(reference))
				&& reference.targetSketchId === targetSketchId
		));
	}

	/**
	 * 同步捕获指定目标草图下仍需参与时间线重建的引用。
	 *
	 * 返回深拷贝使后续仓库新增、删除或状态变化不会改变已经建立的执行计划。
	 *
	 * @param targetSketchIds 本轮 checkpoint 涉及的目标草图。
	 * @returns 按当前仓库存储顺序返回的 retained 引用副本。
	 */
	public captureReferencesByTargetSketchIds(
		targetSketchIds: readonly string[],
	): SketchEntityReferenceRecord[] {
		const targetSketchIdSet = new Set(targetSketchIds);
		if (targetSketchIdSet.size <= 0) {
			return [];
		}
		return this.filterReferences((reference) => (
			isRetainedSketchEntityReferenceRecord(reference)
				&& targetSketchIdSet.has(reference.targetSketchId)
		));
	}

	/**
	 * 按目标实体读取 active 引用记录。
	 *
	 * @param targetSketchId 目标草图 ID。
	 * @param targetEntityId 目标实体 ID。
	 * @returns 命中的引用记录深拷贝。
	 */
	public async listReferencesByTarget(
		targetSketchId: string,
		targetEntityId: string,
	): Promise<SketchEntityReferenceRecord[]> {
		return this.filterReferences((reference) => (
			isActiveSketchEntityReferenceRecord(reference)
				&& reference.targetSketchId === targetSketchId
				&& reference.targetEntityId === targetEntityId
		));
	}

	/**
	 * 更新引用状态。
	 *
	 * @param referenceId 引用记录 ID。
	 * @param status 目标状态。
	 * @param diagnostic dangling/failed 状态对应的结构化原因；省略时保留已有原因。
	 */
	public async updateReferenceStatus(
		referenceId: string,
		status: SketchReferenceStatus,
		diagnostic?: ExecutionDiagnostic,
	): Promise<void> {
		const reference = this.references.get(referenceId);
		if (!reference) {
			throw new Error(`Reference not found: ${referenceId}`);
		}
		const nextDiagnostic = status === 'dangling' || status === 'failed'
			? diagnostic ?? reference.diagnostic
			: undefined;

		this.references.set(referenceId, {
			...reference,
			status,
			diagnostic: nextDiagnostic ? structuredClone(nextDiagnostic) : undefined,
		});
	}

	/**
	 * 替换仍存储的下游草图引用中的来源实体 ID。
	 *
	 * failed 引用仍需同步迁移来源身份，否则下一次来源变更无法重新定位并恢复它。
	 *
	 * @param input 来源实体替换信息。
	 */
	public async replaceSourceEntityId(input: {
		sourceSketchId: string;
		oldEntityId: string;
		newEntityId: string;
	}): Promise<void> {
		assertSketchPersistentId(input.sourceSketchId, 'replaceSourceEntityId.sourceSketchId');
		assertSketchEntityPersistentId(input.oldEntityId, 'replaceSourceEntityId.oldEntityId');
		assertSketchEntityPersistentId(input.newEntityId, 'replaceSourceEntityId.newEntityId');
		Array.from(this.references.entries()).forEach(([referenceId, reference]) => {
			if (
				reference.sourceDescriptor.kind !== 'sketch-entity'
				|| reference.sourceDescriptor.sourceSketchId !== input.sourceSketchId
				|| reference.sourceDescriptor.entityId !== input.oldEntityId
			) {
				return;
			}
			this.references.set(referenceId, {
				...reference,
				sourceDescriptor: {
					...reference.sourceDescriptor,
					entityId: input.newEntityId,
				},
			});
		});
	}

	/**
	 * 删除引用记录。
	 *
	 * @param referenceIds 引用记录 ID 集合。
	 */
	public async removeReferences(referenceIds: string[]): Promise<void> {
		this.removeReferencesImmediately(referenceIds);
	}
}
